import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["identifier"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ButtonGroup } from 'reactstrap';
import { kebabCase } from 'lodash';
import classnames from 'classnames';
import moment from 'moment';
import { conformanceSelector, enabledSelector, labelSelector, newPathSelector, optionsSelector, valueSelector } from '../selectors/index';
import ConformanceTooltip from './conformanceTooltip';
import entityComposition from '../../common/classes/entityComposition';
import { UncomposedSegmentedButton } from './segmentedButton';
import Label from './label';
import logValueOnChange from '../commonActions/logValueOnChange';
import localChangeSelector from '../selectors/localChangeSelector';
const OptionPickerButton = _ref => {
  let _ref$allowsClear = _ref.allowsClear,
    allowsClear = _ref$allowsClear === void 0 ? true : _ref$allowsClear,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$conformanceText = _ref.conformanceText,
    conformanceText = _ref$conformanceText === void 0 ? null : _ref$conformanceText,
    enabled = _ref.enabled,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? '' : _ref$label,
    localChangeFunc = _ref.localChangeFunc,
    onChange = _ref.onChange,
    _ref$options = _ref.options,
    options = _ref$options === void 0 ? [] : _ref$options,
    path = _ref.path,
    _ref$value = _ref.value,
    value = _ref$value === void 0 ? null : _ref$value;
  const showConformance = !conformanceText;
  const handleChange = useCallback(async incomingValue => {
    const newIncomingValue = incomingValue && value && !value[incomingValue.identifier];
    let clearValue = false;
    const mergePatch = {};
    if (value && newIncomingValue) {
      mergePatch[Object.keys(value)[0]] = null;
    }
    // if select button is changing to another selection...
    if (incomingValue && (!value || newIncomingValue)) {
      const storageKey = incomingValue.identifier,
        rest = _objectWithoutProperties(incomingValue, _excluded);
      // const storageKey = incomingValue.identifier;
      mergePatch[storageKey] = _objectSpread(_objectSpread({}, rest), {}, {
        title: incomingValue.title,
        // eslint-disable-next-line no-underscore-dangle
        _created: typeof incomingValue._created !== 'undefined' ? incomingValue._created : moment().toISOString()
      });
    } else if (allowsClear) {
      // if the button is being cleared...
      clearValue = true;
    }
    const keys = Object.keys(mergePatch);
    if (clearValue) {
      await onChange(localChangeFunc({
        path,
        value: null
      }));
    } else if (keys.length > 0) {
      let title;
      keys.forEach(identifier => {
        if (mergePatch[identifier] !== null) {
          title = mergePatch[identifier].title;
        }
      });
      await onChange(localChangeFunc({
        path,
        value: mergePatch,
        displayValue: title
      }));
    } // else: no change - do nothing.
  }, [value, allowsClear, onChange, localChangeFunc, path]);
  const preparedOptions = useMemo(() => {
    const rawOptions = JSON.parse(JSON.stringify(options));
    const newOptions = [];
    rawOptions.forEach((opt, index) => {
      const newOption = JSON.parse(JSON.stringify(opt));
      /* eslint-disable no-underscore-dangle */
      if (typeof newOption._created === 'undefined') {
        newOption._created = index + 1;
      }
      /* eslint-enable no-underscore-dangle */
      newOptions.push(newOption);
    });
    return newOptions;
  }, [options]);
  const preparedValue = useMemo(() => {
    let result = {};
    if (typeof value === 'object' && value !== null && value[Object.keys(value)[0]] !== null) {
      const key = Object.keys(value)[0];
      result = {
        id: key,
        value: (value[key] || {}).title,
        identifier: key
      };
    }
    return result;
  }, [value]);
  const activeValue = preparedValue && typeof preparedValue === 'object' ? preparedValue.identifier : preparedValue;
  const classNames = classnames('tqc-control', 's-opb', 't-opb', className, {
    ["t-opb-".concat(kebabCase(label))]: label !== null && label.length
  }, showConformance ? null : 'is-invalid');
  const id = "t-opb-".concat(kebabCase(label));
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    className: classNames
  }, /*#__PURE__*/React.createElement(Label, {
    value: label
  }), /*#__PURE__*/React.createElement(ButtonGroup, {
    style: {
      display: 'flex'
    }
  }, preparedOptions.map(option => /*#__PURE__*/React.createElement(UncomposedSegmentedButton, {
    key: "SGBTN_".concat(option.identifier),
    active: activeValue === option.identifier,
    enabled: enabled,
    onChange: handleChange,
    option: option,
    label: label
  }))), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: conformanceText,
    target: id,
    placement: "right"
  }));
};
OptionPickerButton.propTypes = {
  allowsClear: PropTypes.bool,
  localChangeFunc: PropTypes.func.isRequired,
  className: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  path: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  conformanceText: PropTypes.string
};
export { OptionPickerButton as UncomposedOptionPickerButton };
export default entityComposition(OptionPickerButton, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  path: newPathSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  localChangeFunc: localChangeSelector
});